/**
 * NOS-239: site-wide announcement bar — org-level chrome config helpers.
 *
 * The config lives in org_customizations.announcement (jsonb). Bench evidence
 * (July 19 2026 audit): beloved (Squarespace) promotes an event with tickets in
 * a dismissible bar above the nav; eastwest (Wix) runs a secondary header
 * strip — one feature serves both. Imports write the config DISABLED with
 * source:"import"; the NOS-214 "announcement_bar" decision card enables it.
 */
import { isDarkBackground } from "@/lib/colorContrast"

export interface AnnouncementConfig {
  enabled: boolean
  text: string
  /** Optional single link (v1: one bar, one link — no scheduling/rotation). */
  link_label?: string
  link_href?: string
  /** Visitors can dismiss the bar (persisted per visitor). Default true. */
  dismissible?: boolean
  /** Background color (hex). Absent = brand primary (NOS-150 token). */
  background?: string
  /** Provenance: "import" when carried in from the source site. */
  source?: string
}

export const ANNOUNCEMENT_TEXT_MAX = 300
export const ANNOUNCEMENT_LINK_LABEL_MAX = 80

/** Validate an untrusted jsonb value into a clean config. Never throws. */
export function parseAnnouncement(value: unknown): AnnouncementConfig | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return null
  const rec = value as Record<string, unknown>
  const text = typeof rec.text === "string" ? rec.text.trim().slice(0, ANNOUNCEMENT_TEXT_MAX) : ""
  if (!text) return null
  const out: AnnouncementConfig = {
    enabled: rec.enabled === true,
    text,
  }
  if (typeof rec.link_label === "string" && rec.link_label.trim() !== "") {
    out.link_label = rec.link_label.trim().slice(0, ANNOUNCEMENT_LINK_LABEL_MAX)
  }
  if (typeof rec.link_href === "string" && rec.link_href.trim() !== "") {
    out.link_href = rec.link_href.trim()
  }
  // dismissible defaults to true; only an explicit false turns it off
  out.dismissible = rec.dismissible !== false
  if (typeof rec.background === "string" && /^#[0-9a-f]{3,8}$/i.test(rec.background.trim())) {
    out.background = rec.background.trim().toLowerCase()
  }
  if (typeof rec.source === "string" && rec.source !== "") out.source = rec.source
  return out
}

/**
 * Per-visitor dismiss key. Includes a content hash so editing the text (a new
 * promo) resurfaces the bar for visitors who dismissed the previous one.
 */
export function announcementDismissKey(orgId: string, config: AnnouncementConfig): string {
  const basis = `${config.text}|${config.link_href ?? ""}`
  let hash = 0
  for (let i = 0; i < basis.length; i++) {
    hash = (hash * 31 + basis.charCodeAt(i)) | 0
  }
  return `nos-announcement-dismissed-${orgId}-${(hash >>> 0).toString(36)}`
}

/**
 * A11y: readable text color for the bar background. Dark backgrounds get
 * white text, light ones near-black (WCAG-luminance threshold via
 * colorContrast). Unparseable colors fall back to white-on-primary.
 */
export function announcementTextColor(background: string | null | undefined): string {
  const dark = isDarkBackground(background)
  if (dark === null) return "#ffffff"
  return dark ? "#ffffff" : "#111111"
}
